<script setup lang="ts">
interface EventStep {
  id: number;
  title: string;
  isValid: boolean;
}

const props = defineProps<{
  currentStep: number;
  isEditEvent: boolean;
  eventSteps: EventStep[];
  bookingStatus?: string;
  trackTickets: boolean;
}>();

const emit = defineEmits<{
  (e: "prev"): void;
  (e: "next"): void;
  (e: "submit"): void;
  (e: "delete"): void;
  (e: "close"): void;
}>();

const showSaveButton = computed(
  () => props.currentStep !== props.eventSteps.length - 1 || props.trackTickets
);

const showNextButton = computed(() => {
  const hasPaymentStep = props.eventSteps.some((el) => el.title === "Payment");

  if (hasPaymentStep) {
    return props.currentStep < props.eventSteps.length - 2
  }
  return props.currentStep < props.eventSteps.length - 1
});
</script>

<template>
  <VCardText>
    <div class="d-flex button_group">
      <div class="button_group__first_btn">
        <VBtn
          :disabled="currentStep === 0"
          color="secondary"
          @click.prevent="emit('prev')"
        >
          <VIcon icon="mdi-arrow-left" start class="flip-in-rtl" />
          Previous
        </VBtn>
      </div>

      <div class="d-flex">
        <!-- Large screen: delete + close -->
        <div class="booking_dynamic_buttons__large">
          <div
            v-if="isEditEvent && bookingStatus !== 'completed'"
            class="button_group__multi_btn"
          >
            <VBtn color="error" @click="emit('delete')">Delete</VBtn>
          </div>
        </div>

        <VBtn
          variant="flat"
          color="secondary"
          class="ml-2 booking_dynamic_buttons__large"
          @click="emit('close')"
        >
          Close
        </VBtn>

        <!-- Edit mode -->
        <div
          v-if="isEditEvent"
          class="button_group__multi_btn align-content-center"
        >
          <VBtn v-if="showSaveButton" color="success" @click="emit('submit')">
            Save
          </VBtn>
        </div>

        <!-- Create mode -->
        <div v-else class="align-content-center">
          <VBtn v-if="showNextButton" class="ml-2" @click="emit('next')">
            Next
            <VIcon icon="mdi-arrow-right" end class="flip-in-rtl" />
          </VBtn>
          <VBtn v-else color="success" class="ml-2" @click="emit('submit')">
            Confirm
          </VBtn>
        </div>
      </div>
    </div>

    <!-- Small screen: close + delete (edit only) -->
    <div
      v-if="isEditEvent"
      class="button_group booking_dynamic_buttons__small align-center"
    >
      <VRow class="d-flex align-center justify-space-between">
        <VCol cols="6">
          <VBtn variant="flat" color="secondary" @click="emit('close')">
            Close
          </VBtn>
        </VCol>
        <VCol cols="6" class="d-flex justify-end">
          <VBtn
            v-if="bookingStatus !== 'completed'"
            color="error"
            @click="emit('delete')"
          >
            Delete
          </VBtn>
        </VCol>
      </VRow>
    </div>
  </VCardText>
</template>

<style scoped lang="scss">
.button_group {
  display: flex;
  justify-content: space-between;

  &__multi_btn {
    padding-inline-start: 0.625rem !important;
  }
}

.booking_dynamic_buttons {
  &__large {
    display: flex;
  }

  &__small {
    display: none !important;
  }
}

@media (max-width: 720px) {
  .button_group {
    &__first_btn {
      padding: 8px;
    }
  }

  .booking_dynamic_buttons {
    &__large {
      display: none;
    }

    &__small {
      display: flex !important;
    }
  }
}
</style>
